import React from 'react'

import {
  alpha,
  Box,
  Card,
  CircularProgress,
  Divider,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material'

import { useThemeMode } from '../../theme/ThemeModeContext'
import { fCurrency, fPercent, fShortenNumber } from '../../utils/formatNumber'
import Iconify from '../Iconify/Iconify'
import { InfoButton } from '../InfoButton'

import { ChangeIndicator } from './ChangeIndicator'

type ValueType = 'number' | 'money' | 'empty' | 'percent'

type BreakdownItem = {
  label: string
  value: number
  type?: ValueType
  icon: string
  changePercent?: number | null
  previousValue?: number | null
}

type WideStatWidgetProps = {
  total: BreakdownItem
  breakdown: BreakdownItem[]
  isLoading?: boolean
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' // You can expand the color options if neede
  sx?: object // This will allow custom styling for the Card component
  type?: 'number' | 'string' | 'money' | 'percent' | 'component'
  explanation?: string | null
}

const IconWrapperStyle = styled('div')(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  width: theme.spacing(8),
  height: theme.spacing(8),
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
}))

const formatValue = (value: number, type: ValueType = 'number') => {
  if (type === 'money') {
    return fCurrency(value, { currency: 'EUR' })
  }

  if (type === 'percent') {
    return fPercent(value)
  }

  return fShortenNumber(value)
}

const formatTooltipValue = (value: number, type: ValueType = 'number') => {
  if (type === 'money') {
    return fCurrency(value, { currency: 'EUR' })
  }

  if (type === 'percent') {
    return fPercent(value)
  }

  return value.toLocaleString()
}

export const WideStatWidget: React.FC<WideStatWidgetProps> = ({
  total,
  breakdown,
  isLoading = false,
  color = 'primary',
  type = 'number',
  sx,
  explanation,
  ...other
}) => {
  const { palette } = useTheme()
  const { resolvedMode } = useThemeMode()

  const colors = {
    light: {
      color: palette[color].main,
      bg: alpha(palette[color].main, palette.action.selectedOpacity),
    },
    dark: {
      color: palette.text.primary,
      bg: palette.background.paper,
    },
  } as const

  const modeColors = colors[resolvedMode]

  return (
    <Card
      sx={{
        height: '100%',
        py: 5,
        boxShadow: 0,
        textAlign: 'center',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: modeColors.color,
        bgcolor: (theme) => modeColors.bg,
        ...sx,
      }}
      {...other}
    >
      {explanation && (
        <Box sx={{ position: 'absolute', top: 15, right: 15 }}>
          <InfoButton explanation={explanation} />
        </Box>
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems="stretch"
        width={'100%'}
        spacing={{ xs: 2, md: 1 }}
        px={1}
      >
        <Stack spacing={1} flex={1} alignItems={'center'} justifyContent={'center'}>
          <IconWrapperStyle
            sx={{
              color: (theme) => theme.palette[color].main,
              backgroundImage: (theme) =>
                `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0)} 0%, ${alpha(
                  theme.palette[color].main,
                  0.24,
                )} 100%)`,
            }}
          >
            <Iconify icon={total.icon} width={24} height={24} />
          </IconWrapperStyle>

          {isLoading ? (
            <CircularProgress disableShrink size={30} />
          ) : (
            <Stack spacing={1} alignItems="center">
              <Typography variant="h3">
                {total.type === 'empty' ? '-' : formatValue(total.value, total.type)}
              </Typography>
              <ChangeIndicator
                value={total.changePercent}
                currentValue={total.type === 'empty' ? null : total.value}
                previousValue={total.previousValue ?? null}
                formatValue={(v) => formatTooltipValue(v, total.type)}
              />
            </Stack>
          )}

          <Typography variant="subtitle2">{total.label}</Typography>
        </Stack>

        <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />

        <Stack
          direction="row"
          spacing={{ xs: 2, md: 1 }}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          justifyContent={'center'}
          sx={{ flex: 2 }}
        >
          {breakdown.map((item) => (
            <Stack
              key={item.label}
              spacing={0.5}
              minWidth={{ xs: 'auto', sm: 120 }}
              alignItems={'center'}
            >
              <IconWrapperStyle
                sx={{
                  width: (theme) => theme.spacing(5),
                  height: (theme) => theme.spacing(5),
                  color: (theme) => theme.palette[color].main,
                  backgroundImage: (theme) =>
                    `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0)} 0%, ${alpha(
                      theme.palette[color].main,
                      0.24,
                    )} 100%)`,
                }}
              >
                <Iconify icon={item.icon} width={18} height={18} />
              </IconWrapperStyle>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{
                  textDecoration: item.type === 'empty' ? 'line-through' : 'none',
                }}
              >
                {item.label}
              </Typography>
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Stack spacing={0.5} alignItems="center">
                  <Typography variant="h6">
                    {item.type === 'empty' ? '-' : formatValue(item.value, item.type)}
                  </Typography>
                  <ChangeIndicator
                    value={item.changePercent || 0}
                    size="small"
                    currentValue={item.type === 'empty' ? null : item.value}
                    previousValue={item.previousValue ?? null}
                    formatValue={(v) => formatTooltipValue(v, item.type)}
                  />
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Card>
  )
}

export default WideStatWidget
